import {
  ApplicationConfig,
  inject,
  Injector,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection
} from "@angular/core";
import { DOCUMENT, LOCATION_INITIALIZED, DatePipe } from "@angular/common";
import {
  PreloadAllModules,
  provideRouter,
  withPreloading
} from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { provideTranslateService, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideEffects } from "@ngrx/effects";
import { provideState, provideStore } from "@ngrx/store";
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { NgxMaskConfig, provideNgxMask } from "ngx-mask";
import {
  IHotjarSettings,
  NGX_HOTJAR_INITIALIZER_PROVIDER,
  NGX_HOTJAR_SETTINGS_TOKEN
} from "ngx-hotjar";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE
} from "@angular/material/core";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { OverlayContainer } from "@angular/cdk/overlay";

import { environment } from "../environments/environment";
import { initializeRoutes, rootRoutes } from "./app.routes";
import { BookingEffects, bookingReducer } from "./state";
import { baseUrlInterceptor } from "./interceptors/baseUrl.interceptor";
import { CustomOverlayContainer } from "./classes/custom-overlay-container";
import { ApplicationInsightsService } from "./services/application-insights.service";
import { FeatureAppService } from "./services/feature-app.service";
import { provideDevHelpers } from "./dev-helpers";

const maskConfig: Partial<NgxMaskConfig> = {
  validation: false
};

export const DateFormats = {
  parse: {
    dateInput: ["YYYY-MM-DD"]
  },
  display: {
    dateInput: "YYYY-MM-DD",
    monthYearLabel: "MMM YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "MMMM YYYY"
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(rootRoutes, withPreloading(PreloadAllModules)),
    provideHttpClient(withInterceptors([baseUrlInterceptor])),

    // Translations — provideTranslateService sets up the service;
    // provideTranslateHttpLoader provides the TranslateLoader token.
    provideTranslateService({ defaultLanguage: "se" }),
    ...provideTranslateHttpLoader({
      prefix: "/assets/i18n/",
      suffix: ".json",
      enforceLoading: true
    }),

    // NgRx
    provideStore(),
    provideState("booking", bookingReducer),
    provideEffects([BookingEffects]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      connectInZone: false
    }),

    // ngx-mask
    provideNgxMask(maskConfig),

    // Hotjar
    {
      provide: NGX_HOTJAR_SETTINGS_TOKEN,
      useValue: {
        trackingCode: environment.hotjarId
      } as IHotjarSettings
    },
    NGX_HOTJAR_INITIALIZER_PROVIDER,

    // Google Maps — dynamically inject the Maps JS API loader script
    provideAppInitializer(() => {
      const doc = inject(DOCUMENT);
      const script = doc.createElement("script");
      script.innerHTML = `
        (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({key: "${environment.mapsKey}", v: "weekly"});
      `;
      doc.head.appendChild(script);
    }),

    // MarkerClusterer — required by MapMarkerClusterer from @angular/google-maps
    provideAppInitializer(() => {
      const doc = inject(DOCUMENT);
      const script = doc.createElement("script");
      script.src =
        "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
      doc.head.appendChild(script);
    }),

    // Google Tag Manager
    { provide: "googleTagManagerId", useValue: environment.gtmKey },

    // Material date adapter
    { provide: MAT_DATE_LOCALE, useValue: "sv_SE" },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    { provide: MAT_DATE_FORMATS, useValue: DateFormats },

    // Custom CDK overlay container (scoped to the web component shadow root)
    { provide: OverlayContainer, useClass: CustomOverlayContainer },

    // Built-in pipes used as injectable services
    DatePipe,

    // App initializers
    provideAppInitializer(() => {
      const translate = inject(TranslateService);
      const injector = inject(Injector);
      const faService = inject(FeatureAppService);
      return appInitializerFactory(translate, injector, faService)();
    }),
    provideAppInitializer(() => {
      const svc = inject(ApplicationInsightsService);
      return svc.init ? svc.init() : void 0;
    }),
    provideAppInitializer(() => {
      return initializeRoutes();
    }),
    // DevHelpers for console state management in development mode
    provideAppInitializer(() => {
      provideDevHelpers();
    })
  ]
};

export function appInitializerFactory(
  translate: TranslateService,
  injector: Injector,
  faService: FeatureAppService
) {
  return () =>
    new Promise((resolve) => {
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );
      locationInitialized.then(() => {
        const langToSet = "se";
        translate.setFallbackLang("se");
        translate.use(langToSet).subscribe({
          next: () => {
            console.log(`Successfully initialized '${langToSet}' language.'`);
          },
          error: () => {
            console.error(
              `Problem with '${langToSet}' language initialization.'`
            );
          },
          complete: () => {
            if (!faService.isFeatureApp) {
              faService.initialize();
              resolve(null);
            } else {
              const intervalId = setInterval(() => {
                if (window.featureAppConfig) {
                  clearInterval(intervalId);
                  console.log("resolved fa config");
                  faService.initialize();
                  resolve(null);
                }
              }, 100);
            }
          }
        });
      });
    });
}
